import React from 'react';
import { useAccess } from '../../hooks/useAccess';
import { useTenant } from '../../hooks/useTenant';
import { tenantRoleLabel } from '../../config/tenantRoles';
import { Chip } from '../ui/Chip';
import { SectionCard } from '../ui/SectionCard';

// Business Case names read "EPÜ — Intelligent facility …"; the part before
// the dash is the short name.
const shortName = (businessCase) => String(businessCase.name || businessCase.code).split(' — ')[0];

const PersonaRow = ({ persona, businessCases, acting }) => {
  const scope = Array.isArray(persona.businessCases)
    ? persona.businessCases.map((code) => businessCases.find((bc) => bc.code === code) || { code, name: code })
    : businessCases;

  return (
    <li className="flex flex-col gap-1.5 py-3 border-t border-black/[0.07] first:border-t-0 first:pt-0 last:pb-0">
      <span className="flex flex-wrap items-center gap-2 text-[14px] font-semibold text-lb-ink">
        {persona.label}
        {acting && <Chip variant="warning">Acting as</Chip>}
      </span>
      {scope.length > 0 && (
        <span className="flex flex-wrap gap-1.5">
          {scope.map((bc) => (
            <Chip key={bc.code} variant="neutral" title={bc.name}>
              {bc.code === shortName(bc) ? bc.code : `${bc.code} · ${shortName(bc)}`}
            </Chip>
          ))}
        </span>
      )}
    </li>
  );
};

/**
 * What the signed-in user can open: their role in the active organisation and
 * the personas they hold per Business Case (GET /access/me). Read-only; the
 * assignments are made in Keycloak.
 */
export const AccessCard = () => {
  const { activeTenant } = useTenant();
  const { loading, bypass, personas, activePersona, businessCases } = useAccess();
  const organisation = activeTenant?.name || activeTenant?.slug;

  let personaContent;
  if (loading) {
    personaContent = <p className="m-0 text-[13px] text-lb-muted">Loading your access…</p>;
  } else if (bypass) {
    personaContent = (
      <p className="m-0 text-[13px] text-lb-body">
        You are a platform administrator and can open every Business Case and service.
      </p>
    );
  } else if (personas.length === 0) {
    personaContent = (
      <p className="m-0 text-[13px] text-lb-body">
        You have no persona yet, so no AI services are open to you. Ask the Hub administrator to assign
        you to a Business Case.
      </p>
    );
  } else {
    personaContent = (
      <ul className="m-0 p-0 list-none flex flex-col" aria-label="Your personas">
        {personas.map((persona) => (
          <PersonaRow
            key={persona.role}
            persona={persona}
            businessCases={businessCases}
            acting={personas.length > 1 && persona.role === activePersona}
          />
        ))}
      </ul>
    );
  }

  return (
    <SectionCard title="Your access" subtitle="What you can open in the Hub, and where.">
      <div className="flex flex-col gap-4">
        <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-4 gap-y-2">
          <dt className="text-[12px] font-semibold text-lb-muted">Organisation</dt>
          <dd className="m-0 flex flex-wrap items-center gap-2 text-[14px] font-semibold text-lb-ink">
            {organisation || 'None'}
            {activeTenant?.role && <Chip variant="active">{tenantRoleLabel(activeTenant.role)}</Chip>}
          </dd>
        </dl>
        {personaContent}
        {!bypass && (
          <p className="m-0 text-[12px] leading-[1.45] text-lb-muted">
            Personas and Business Cases are assigned by the Hub administrator.
            {personas.length > 1 && ' Switch the persona you act as from the sidebar.'}
          </p>
        )}
      </div>
    </SectionCard>
  );
};
